import { ReactElement } from 'react';
import { Message, useMessage } from '@messageformat/react';
import { Tile } from '@op/opds.opux';
import { formatNumberWithoutDecimals, getMsgValues } from '../../../utils';
import styles from './ProductSummaryTile.module.scss';

interface Props {
  buySum: number;
  sellSum: number;
}

const SummaryText = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}): ReactElement => (
  <div data-testid={label} className={styles['text-content']}>
    <p className={styles['label-text']} style={{ color }}>
      {label}
    </p>
    <p className={styles['body-text']} style={{ color }}>
      {value} €
    </p>
  </div>
);

const formatFinalProfit = (finalProfitValue: number): string => {
  return finalProfitValue > 0
    ? `+${formatNumberWithoutDecimals(finalProfitValue)}`
    : formatNumberWithoutDecimals(finalProfitValue);
};

const formatGainTax = (gainTaxValue: number): string => {
  return gainTaxValue > 0 ? `−${formatNumberWithoutDecimals(gainTaxValue)}` : '0';
};

const getLabelText = (
  gainIndex: number,
  lossIndex: number,
  gainValue: number,
  textArray: string[],
): string => {
  return gainValue >= 0 ? textArray[gainIndex] : textArray[lossIndex];
};

const ProductSummaryTile = ({ buySum, sellSum }: Props): ReactElement => {
  const summaryText = getMsgValues(useMessage('course9.task1.summary_text'));
  const capitalGain = sellSum - buySum;
  const gainTax = capitalGain > 0 ? capitalGain * 0.3 : 0;
  const finalProfit = capitalGain - gainTax;

  return (
    <Tile className={styles['tile-container']}>
      <div className={styles['tile-content']}>
        <h4 className={styles['summary-title']}>
          <Message id="course9.task1.summary_title" />
        </h4>
        <div className="investoracademy-divider-line" />
        <SummaryText label={summaryText[0]} value={formatNumberWithoutDecimals(buySum)} />
        <SummaryText
          label={summaryText[1]}
          value={formatNumberWithoutDecimals(sellSum)}
        />
        <div className="investoracademy-divider-line" />
        <SummaryText
          label={getLabelText(2, 5, capitalGain, summaryText)}
          value={formatNumberWithoutDecimals(capitalGain)}
        />
        <SummaryText
          label={getLabelText(3, 6, capitalGain, summaryText)}
          value={formatGainTax(gainTax)}
          color="#CD212E"
        />
        <div className="investoracademy-divider-line" />
        <SummaryText
          label={getLabelText(4, 7, capitalGain, summaryText)}
          value={formatFinalProfit(finalProfit)}
          color="#007F25"
        />
      </div>
    </Tile>
  );
};

export default ProductSummaryTile;
